import { yupResolver } from '@hookform/resolvers/yup';
import { isArray, isObject, isString, noop } from '@src/shared/lib';
import { is400 } from '@src/shared/lib/httpUtils';
import { isPromise } from '@src/shared/lib/validationUtils';
import { getServerErrorMessage } from '@src/shared/lib/error_handlers';
import { useCallback, useRef } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm as useFormBase } from 'react-hook-form';

interface YupSchema {
  type: string;
  fields: Record<string, YupSchema>;
  innerType: YupSchema;
}

interface UseFormParams {
  schema?: YupSchema;
  defaultValues?: Record<string, unknown>;
  showServerErrors?: boolean;
  mapServerErrorField?: (field: string) => string;
  onNonFieldError?: (messages: string[]) => void;
  [key: string]: unknown;
}

interface SubmitCallbacks {
  onSubmit?: (values: FieldValues) => unknown;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown, info: { isInvalid: boolean }) => void;
  onInvalid?: () => void;
}

function getErrorResponse(error: {
  response: { data: Record<string, unknown> };
}): Record<string, unknown> {
  const responseData = error.response.data;
  return (
    'payload' in responseData ? responseData.payload : responseData
  ) as Record<string, unknown>;
}

function hasPath(value: unknown, path: string): boolean {
  const parts = path.replace(/\[(\d+)]/g, '.$1').split('.').filter(Boolean);
  let current = value;

  for (const part of parts) {
    if (current === null || typeof current !== 'object' || !(part in current)) {
      return false;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return true;
}

function useForm(params: UseFormParams) {
  const {
    schema,
    defaultValues,
    showServerErrors = true,
    mapServerErrorField,
    onNonFieldError,
    ...restParams
  } = params || {};

  const counter = useRef<number>(0);

  const {
    setError,
    handleSubmit: handleSubmitBase,
    setValue,
    reset,
    getValues,
    ...form
  } = useFormBase({
    ...restParams,
    defaultValues,
    resolver: schema
      ? yupResolver(schema as never, { context: { form: { defaultValues } } })
      : undefined,
  });

  const setErrors = useCallback(
    (data: Record<string, unknown>) => {
      counter.current = 0;
      const nonFieldMessages: string[] = [];
      const formValues = getValues();
      let fieldErrorCount = 0;

      const addError = (path: string, message: string) => {
        if (hasPath(formValues, path)) {
          setError(path, {
            type: 'server',
            message,
          });
          fieldErrorCount += 1;
          return;
        }

        nonFieldMessages.push(message);
      };

      const parseErrors = (
        d: Record<string, unknown>,
        p = '',
      ) => {
        if (counter.current >= 10000) {
          throw new Error('Unable to parse object.');
        }
        counter.current += 1;

        for (const pair of Object.entries(d)) {
          const [key, value] = pair;
          const normalizedKey = mapServerErrorField?.(key) || key;
          const errorKey = p ? `${p}.${normalizedKey}` : normalizedKey;
          if (isString(value)) {
            addError(errorKey, value);
          } else if (isArray(value)) {
            const messages = value.filter(isString);
            if (messages.length > 0) {
              addError(errorKey, messages.join(' '));
            }
            value.forEach((item, index) => {
              if (isObject(item)) {
                parseErrors(item, `${errorKey}[${index}]`);
              }
            });
          } else if (isObject(value)) {
            parseErrors(value, errorKey);
          }
        }
      };

      try {
        parseErrors(data);
        if (nonFieldMessages.length > 0) {
          onNonFieldError?.([...new Set(nonFieldMessages)]);
        }
      } catch (error) {
        console.error('Unable to parse errors.', error);
      }

      return {
        fieldErrorCount,
        nonFieldErrorCount: nonFieldMessages.length,
      };
    },
    [getValues, mapServerErrorField, onNonFieldError, setError],
  );

  const handleSubmit = useCallback(
    ({
      onSubmit = noop,
      onSuccess = noop,
      onError = noop,
      onInvalid = noop,
    }: SubmitCallbacks) => {
      return handleSubmitBase((values) => {
        const promise = onSubmit(values);
        if (isPromise(promise)) {
          return new Promise<void>((resolve) => {
            (promise as Promise<unknown>)
              .then((response: unknown) => {
                onSuccess(response);
                resolve();
              })
              .catch((error: unknown) => {
                const err = error as {
                  response?: {
                    status?: number | string | null;
                    data: Record<string, unknown>;
                  };
                };
                if (is400(err?.response)) {
                  if (showServerErrors) {
                    const parsedErrors = setErrors(
                      getErrorResponse(
                        err as { response: { data: Record<string, unknown> } },
                      ),
                    );
                    if (
                      parsedErrors.fieldErrorCount === 0
                      && parsedErrors.nonFieldErrorCount === 0
                    ) {
                      onNonFieldError?.([getServerErrorMessage(error)]);
                    }
                  }
                  onError(error, { isInvalid: true });
                } else {
                  console.error('Uexpected form error.', error);
                  if (showServerErrors) {
                    onNonFieldError?.([getServerErrorMessage(error)]);
                  }
                  onError(error, { isInvalid: false });
                }
                resolve();
              });
          });
        }

        console.log(
          'Non Promise result. Form submitting attributes may not working.',
        );
        return Promise.resolve();
      }, onInvalid);
    },
    [handleSubmitBase, onNonFieldError, setErrors, showServerErrors],
  );

  return {
    setError,
    setErrors,
    handleSubmitBase,
    handleSubmit,
    reset,
    setValue,
    getValues,
    ...form,
  };
}

export default useForm;
